import type { QueryClient } from '@tanstack/react-query';
import type { PagedResponse, PaginatedListParams } from '../types';

export const DEFAULT_PAGE_SIZE = 20;
export const MAX_PAGE_SIZE = 100;

export const removeItemFromPaginatedQueries = <T extends { id: string }>(
  queryClient: QueryClient,
  queryKey: readonly unknown[],
  itemId: string,
): void => {
  queryClient.setQueriesData<PagedResponse<T>>(
    { queryKey },
    (response: PagedResponse<T> | undefined) => {
      if (!response?.content) {
        return response;
      }

      const content = response.content.filter((item: T) => item.id !== itemId);
      if (content.length === response.content.length) {
        return response;
      }

      const { size, totalElements } = getPagedMeta(response);
      const nextTotalElements = Math.max(0, totalElements - 1);
      const nextTotalPages = size ? Math.ceil(nextTotalElements / size) : 0;

      return {
        ...response,
        content,
        ...(response.meta
          ? {
              meta: {
                ...response.meta,
                totalElements: nextTotalElements,
                totalPages: nextTotalPages,
              },
            }
          : {}),
        ...(response.totalElements !== undefined
          ? { totalElements: nextTotalElements }
          : {}),
        ...(response.totalPages !== undefined
          ? { totalPages: nextTotalPages }
          : {}),
      };
    },
  );
};

export const buildPaginationQuery = ({
  page,
  size,
  search,
  excludeDeleted,
}: PaginatedListParams): string => {
  const params = new URLSearchParams({
    page: String(page),
    size: String(size),
  });
  const normalizedSearch = search?.trim();
  if (normalizedSearch) {
    params.set('search', normalizedSearch);
  }
  if (excludeDeleted !== undefined) {
    params.set('excludeDeleted', String(excludeDeleted));
  }
  return params.toString();
};

export const getPagedMeta = <T>(response?: PagedResponse<T>) => {
  const totalElements =
    response?.meta?.totalElements ?? response?.totalElements ?? 0;
  const totalPages = response?.meta?.totalPages ?? response?.totalPages ?? 0;
  const page = response?.meta?.page ?? response?.number ?? 0;
  const size = response?.meta?.size ?? response?.size ?? DEFAULT_PAGE_SIZE;

  return {
    page,
    size,
    totalElements,
    totalPages,
  };
};

export const createEmptyPagedResponse = <T>({
  page,
  size,
}: PaginatedListParams): PagedResponse<T> => ({
  content: [],
  meta: {
    page,
    size,
    totalElements: 0,
    totalPages: 0,
  },
});
